import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AppShell } from '@/components/app-shell';
import { RequireRole } from '@/components/require-role';
import { RequireFeature } from '@/components/require-feature';
import { LoginPage } from '@/features/auth/login-page';
import { OnboardingPage } from '@/features/onboarding/onboarding-page';
import { OnboardingWizard } from '@/features/onboarding/onboarding-wizard';
import { DashboardPage } from '@/features/dashboard/dashboard-page';
import { TasksPage } from '@/features/tasks/tasks-page';
import { TaskDetailPage } from '@/features/tasks/task-detail-page';
import { CustomersPage } from '@/features/customers/customers-page';
import { CustomerDetailPage } from '@/features/customers/customer-detail-page';
import { InventoryPage } from '@/features/inventory/inventory-page';
import { PriceListPage } from '@/features/price-list/price-list-page';
import { InvoicesPage } from '@/features/invoices/invoices-page';
import { SettingsPage } from '@/features/settings/settings-page';
import { TaskStatusPage } from '@/features/public/task-status-page';
import { BookingPage } from '@/features/public/booking-page';
import { QuotePage } from '@/features/public/quote-page';
import { AboutPage, PrivacyPage, TermsPage } from '@/features/public/legal-pages';
import { LandingPage } from '@/features/public/landing-page';
import { QuotesPage } from '@/features/quotes/quotes-page';
import { ProfitabilityPage } from '@/features/reports/profitability-page';
import { MaintenancePage } from '@/features/maintenance/maintenance-page';
import { ProcessesPage } from '@/features/processes/processes-page';
import { WorkflowsPage } from '@/features/workflows/workflows-page';
import { SchedulePage } from '@/features/schedule/schedule-page';
import { SuppliersPage } from '@/features/suppliers/suppliers-page';
import { ContractsPage } from '@/features/contracts/contracts-page';
import { PurchaseOrdersPage } from '@/features/purchasing/purchase-orders-page';
import { SupplierInvoicesPage } from '@/features/purchasing/supplier-invoices-page';
import { AdminPage } from '@/features/admin/admin-page';
import { LearnPage } from '@/features/learning/learn-page';
import { MfaSetupGate, SecurityPage } from '@/features/auth/security-page';
import { AssistantPage } from '@/features/assistant/assistant-page';
import { tokenStore } from '@/lib/api';
import type { UserRole } from '@/lib/schemas';

/**
 * ניתוב מבוסס-קוד ולא מבוסס-קבצים: אין קובץ `routeTree.gen.ts`
 * מיוצר שצריך לזכור לוודא שהוא מסונכרן, והמסלולים גלויים במקום אחד.
 */

/**
 * מי רואה את מסכי העבודה: אדמין, עריכה וצפייה בלבד (השרת נותן לצופה GET על
 * כל מה שמנהל קורא). כפתורי הכתיבה מוסתרים לצופה במסכים עצמם (useCanEdit).
 * ההגדרות פתוחות לכולם — אבל מי שאינו אדמין רואה שם רק את הפרופיל שלו.
 */
const READERS: UserRole[] = ['OWNER', 'MANAGER', 'VIEWER'];

const rootRoute = createRootRoute({ component: Outlet });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // משתמש עם טוקן שמנווט ל-/login מוחזר פנימה. בלי זה, "חזור"
  // בדפדפן אחרי התחברות מציג שוב את מסך ההתחברות.
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router's contract: beforeLoad throws the Redirect (a Response, not an Error)
    if (tokenStore.get()) throw redirect({ to: '/' });
  },
  validateSearch: (
    search: Record<string, unknown>,
  ): { redirect?: string; google_ticket?: string; google_error?: string } => ({
    redirect: typeof search['redirect'] === 'string' ? search['redirect'] : undefined,
    // חזרה מ"התחברות עם Google" (GoogleLoginController.callback).
    google_ticket: typeof search['google_ticket'] === 'string' ? search['google_ticket'] : undefined,
    google_error: typeof search['google_error'] === 'string' ? search['google_error'] : undefined,
  }),
  component: LoginPage,
});

/**
 * מסלול הגנה. חשוב: זו הסתרת UI בלבד — הטוקן נבדק בשרת בכל בקשה.
 * בדיקת קליינט לעולם אינה גבול אבטחה.
 */
/**
 * הקמת עסק חדש. ציבורי במכוון — ההרשמה קורית לפני שיש טננט או משתמש.
 * הוא *לא* יושב תחת protectedRoute: המסך עצמו מחליט לפי מצב ההתחברות
 * ושלב ההצטרפות מה להציג (ADR 0002).
 */
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  // `manual` — האשף הידני במקום הקמה בשיחה (ברירת המחדל). `connected` — חזרה מ-Google.
  // `google_ticket` / `google_error` — חזרה מ-Google בהרשמה מתוך השיחה.
  validateSearch: (
    search: Record<string, unknown>,
  ): { manual?: boolean; connected?: string; google_ticket?: string; google_error?: string } => ({
    ...(search['manual'] === true || search['manual'] === 'true' || search['manual'] === 1 || search['manual'] === '1'
      ? { manual: true }
      : {}),
    ...(typeof search['connected'] === 'string' ? { connected: search['connected'] } : {}),
    ...(typeof search['google_ticket'] === 'string' ? { google_ticket: search['google_ticket'] } : {}),
    ...(typeof search['google_error'] === 'string' ? { google_error: search['google_error'] } : {}),
  }),
  component: OnboardingWizard,
});

/** המסלול הישן — הקמה בשיחה, בלי Google. */
const onboardingChatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/chat',
  component: OnboardingPage,
});

/**
 * דפים שהלקוח של העסק פותח מקישור ב-WhatsApp. בלי התחברות ובלי מעטפת
 * האפליקציה: הטוקן בכתובת הוא ההרשאה, והשרת מאמת אותו בכל בקשה.
 */
const publicStatusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/c/s/$token',
  component: TaskStatusPage,
});

const publicBookingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/c/b/$token',
  component: BookingPage,
});

const publicQuoteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/c/q/$token',
  component: QuotePage,
});

/** דף בית, פרטיות ותנאים — ציבוריים, כפי ש-Google דורשת לאימות אפליקציית ה-OAuth. */
const welcomeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/welcome', component: LandingPage });
const aboutRoute = createRoute({ getParentRoute: () => rootRoute, path: '/about', component: AboutPage });
const privacyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/privacy', component: PrivacyPage });
const termsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/terms', component: TermsPage });

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  beforeLoad: ({ location }) => {
    if (!tokenStore.get()) {
      // מבקר בלי חשבון בכתובת הראשית — דף הנחיתה, לא מסך הכניסה.
      if (location.pathname === '/') {
        // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router's contract: beforeLoad throws the Redirect (a Response, not an Error)
        throw redirect({ to: '/welcome' });
      }
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router's contract: beforeLoad throws the Redirect (a Response, not an Error)
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  component: () => (
    <AppShell>
      <MfaSetupGate>
        <Outlet />
      </MfaSetupGate>
    </AppShell>
  ),
});

/** אבטחת החשבון — לכל תפקיד. */
const securityRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/security',
  component: SecurityPage,
});

const indexRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  component: () => (
    <RequireRole roles={READERS}>
      <DashboardPage />
    </RequireRole>
  ),
});

const tasksRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/tasks',
  component: () => (
    <RequireRole roles={READERS}>
      <TasksPage />
    </RequireRole>
  ),
});

const taskDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/tasks/$taskId',
  component: () => (
    <RequireRole roles={READERS}>
      <TaskDetailPage />
    </RequireRole>
  ),
});

const customersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/customers',
  component: () => (
    <RequireRole roles={READERS}>
      <RequireFeature feature="customers">
        <CustomersPage />
      </RequireFeature>
    </RequireRole>
  ),
});

const customerDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/customers/$customerId',
  component: () => (
    <RequireRole roles={READERS}>
      <RequireFeature feature="customers">
        <CustomerDetailPage />
      </RequireFeature>
    </RequireRole>
  ),
});

const priceListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/price-list',
  component: () => (
    <RequireRole roles={READERS}>
      <RequireFeature feature="priceList">
        <PriceListPage />
      </RequireFeature>
    </RequireRole>
  ),
});

const profitabilityRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/profitability',
  component: () => (
    <RequireRole roles={READERS}>
      <RequireFeature feature="invoicing">
        <ProfitabilityPage />
      </RequireFeature>
    </RequireRole>
  ),
});

const quotesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/quotes',
  component: () => (
    <RequireRole roles={READERS}>
      <RequireFeature feature="quotes">
        <QuotesPage />
      </RequireFeature>
    </RequireRole>
  ),
});

const maintenanceRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/maintenance',
  component: () => (
    <RequireRole roles={READERS}>
      <RequireFeature feature="equipment">
        <MaintenancePage />
      </RequireFeature>
    </RequireRole>
  ),
});

const inventoryRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/inventory',
  component: () => (
    <RequireRole roles={READERS}>
      <RequireFeature feature="inventory">
        <InventoryPage />
      </RequireFeature>
    </RequireRole>
  ),
});

const invoicesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/invoices',
  component: () => (
    <RequireRole roles={READERS}>
      <RequireFeature feature="invoicing">
        <InvoicesPage />
      </RequireFeature>
    </RequireRole>
  ),
});

/** מרקטפלייס התהליכים (ADR 0003). */
const processesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/processes',
  component: () => (
    <RequireRole roles={READERS}>
      <ProcessesPage />
    </RequireRole>
  ),
});

/** "התהליכים שלי" — כללי אוטומציה שהעסק מגדיר (src/modules/workflows). */
const workflowsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/workflows',
  component: () => (
    <RequireRole roles={READERS}>
      <WorkflowsPage />
    </RequireRole>
  ),
});

// `/settings` ו-`/settings/$tab` שניהם מגיעים ל-SettingsPage: ה-callback
// של Google מפנה ל-/settings/integrations, ובלי הנתיב השני זה 404.
const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings',
  component: () => (
    <RequireRole roles={READERS}>
      <SettingsPage />
    </RequireRole>
  ),
});

const suppliersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/suppliers',
  component: () => (
    <RequireRole roles={READERS}>
      <RequireFeature feature="suppliers">
        <SuppliersPage />
      </RequireFeature>
    </RequireRole>
  ),
});

const contractsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/contracts',
  component: () => (
    <RequireRole roles={READERS}>
      <RequireFeature feature="contracts">
        <ContractsPage />
      </RequireFeature>
    </RequireRole>
  ),
});

const purchaseOrdersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/purchase-orders',
  component: () => (
    <RequireRole roles={READERS}>
      <RequireFeature feature="purchaseOrders">
        <PurchaseOrdersPage />
      </RequireFeature>
    </RequireRole>
  ),
});

const supplierInvoicesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/supplier-invoices',
  component: () => (
    <RequireRole roles={READERS}>
      <RequireFeature feature="supplierInvoices">
        <SupplierInvoicesPage />
      </RequireFeature>
    </RequireRole>
  ),
});

const scheduleRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/schedule',
  component: () => (
    <RequireRole roles={READERS}>
      <RequireFeature feature="scheduling">
        <SchedulePage />
      </RequireFeature>
    </RequireRole>
  ),
});

const settingsTabRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings/$tab',
  component: () => (
    <RequireRole roles={READERS}>
      <SettingsPage />
    </RequireRole>
  ),
});

/** קונסולת מפעיל הפלטפורמה. המסך עצמו מציג "לא נמצא" למי שאינו מפעיל; השרת מחזיר 403. */
/** "למידה ממסמכים" — בעלים ומנהל, בכל שלב. `?run=` — ריצה מסוימת. */
const learnRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/learn',
  validateSearch: (search: Record<string, unknown>): { run?: string } =>
    typeof search.run === 'string' && /^[0-9a-f-]{36}$/i.test(search.run) ? { run: search.run } : {},
  component: function LearnRouteComponent() {
    const { run } = learnRoute.useSearch();
    return (
      <RequireRole roles={['OWNER', 'MANAGER']}>
        <LearnPage key={run ?? 'latest'} runId={run} />
      </RequireRole>
    );
  },
});

/** העוזר במסך מלא, עם היסטוריית השיחות. `?c=` — השיחה הפתוחה. כל תפקיד שרואה את האתר. */
const assistantRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/assistant',
  validateSearch: (search: Record<string, unknown>): { c?: string } =>
    typeof search['c'] === 'string' && /^[0-9a-f-]{36}$/i.test(search['c']) ? { c: search['c'] } : {},
  component: function AssistantRouteComponent() {
    const { c } = assistantRoute.useSearch();
    return (
      <RequireRole roles={READERS}>
        <AssistantPage conversationId={c ?? null} />
      </RequireRole>
    );
  },
});

const adminRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/admin',
  component: AdminPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  onboardingRoute,
  onboardingChatRoute,
  publicStatusRoute,
  publicBookingRoute,
  publicQuoteRoute,
  welcomeRoute,
  aboutRoute,
  privacyRoute,
  termsRoute,
  protectedRoute.addChildren([
    indexRoute,
    tasksRoute,
    taskDetailRoute,
    customersRoute,
    customerDetailRoute,
    inventoryRoute,
    maintenanceRoute,
    quotesRoute,
    profitabilityRoute,
    priceListRoute,
    invoicesRoute,
    processesRoute,
    workflowsRoute,
    settingsRoute,
    settingsTabRoute,
    scheduleRoute,
    suppliersRoute,
    contractsRoute,
    purchaseOrdersRoute,
    supplierInvoicesRoute,
    adminRoute,
    securityRoute,
    learnRoute,
    assistantRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
